import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { EventCardComponent } from '../../components/event-card/event-card.component';
import { NzInputNumberModule } from 'ng-zorro-antd/input-number';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { AddToCartRequest, Cart, CartItem, EventModel } from '../../models';
import {
  ApiService,
  SessionService,
  CartStateService,
  CHECKOUT_PROFILE_PROVIDER,
} from '../../services';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { ViewOneSkeletonComponent } from '../view-one-skeleton/view-one-skeleton.component';
import { NzImageModule } from 'ng-zorro-antd/image';
import { environment } from '@env/environment';
import {
  EventTicketType,
  TicketTypeAddOn,
  TicketTypeAddOnSelection,
} from '../../models/event-ticket-type.interface';
import { isTicketAddable } from '../../models/ticket-status.util';
import { TicketSelectionComponent } from '../../components/ticket-selection/ticket-selection.component';
import { SeatMapPickerComponent } from '../../components/seat-map-picker/seat-map-picker.component';
import { EventSeatMap, SeatMapBay } from '../../models/event-seat-map.interface';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { FormsModule } from '@angular/forms';
import { EventField } from '../../models/event-field-type.interface';
import { NzModalModule, NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { AddToCartComponent } from '../../components/add-to-cart/add-to-cart.component';
import {
  NzNotificationModule,
  NzNotificationService,
} from 'ng-zorro-antd/notification';
import { QuantityComponentComponent } from '../../components/quantity-component/quantity-component.component';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzDrawerModule, NzDrawerService } from 'ng-zorro-antd/drawer';
import { IndemityComponent } from '../indemity/indemity.component';
import { AuthStateService } from '@hypenation/auth';
import { Microservice, FilesService } from '@hypenation/shared';
import { timeout } from 'rxjs';

const SEAT_MAP_TIMEOUT_MS = 5000;
@Component({
  selector: 'hypenation-tickets-view-one',
  standalone: true,
  imports: [
    CommonModule,
    EventCardComponent,
    NzInputNumberModule,
    NzButtonModule,
    NzMenuModule,
    NzDropDownModule,
    NzIconModule,
    ViewOneSkeletonComponent,
    NzImageModule,
    TicketSelectionComponent,
    SeatMapPickerComponent,
    NzSpinModule,
    FormsModule,
    NzNotificationModule,
    QuantityComponentComponent,
    NzCheckboxModule,
    FormsModule,
    NzDrawerModule,
    NzModalModule,
  ],
  templateUrl: './view-one.component.html',
  styleUrl: './view-one.component.css',
  providers: [NzModalService],
})
export class ViewOneComponent implements OnInit {
  events!: EventModel[];
  selectedEvent!: EventModel;
  visible = false;
  apiUrl: string = environment.apiUrl;
  eventTickets!: EventTicketType[];
  selectedTicketType: EventTicketType | null = null;
  /** The venue diagram with per-bay price and availability; null for an event without a seat map. */
  seatMap: EventSeatMap | null = null;
  loadingSeatMap = false;
  loadingTickets = false;
  numberOfTickets = 1;
  fields: EventField[] = [];
  fileDownloadUrl = environment.fileDownloadUrl;
  sessionId!: string;
  cart: Cart | null = null;
  cartItems: CartItem[] = [];
  eventImageUrl: string = '';
  loadingAddtocart = false;
  loadingCart = false;
  loadingAddOns = false;
  ticketTypeAddOns: TicketTypeAddOn[] = [];
  selectedAddOnUids = new Set<string>();
  addOnsModalVisible = false;

  private readonly sanitizer = inject(DomSanitizer);

  get safeDetails(): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(
      this.selectedEvent?.details ?? ''
    );
  }

  get statusBanner(): { title: string; message: string } | null {
    switch (this.selectedEvent?.status) {
      case 'PASSED':
        return {
          title: 'This event has concluded',
          message:
            'Ticket sales are closed. You can still view the event details below, but no tickets can be purchased.',
        };
      case 'CLOSED':
        return {
          title: 'Ticket sales have closed',
          message:
            'Ticket sales for this event have closed. No further purchases can be made.',
        };
      case 'POSTPONED':
        return {
          title: 'This event has been postponed',
          message:
            'New dates have not yet been confirmed. Please check back later for updates.',
        };
      default:
        return null;
    }
  }

  requiresIndemnity = false;
  userProfile: any = null;
  isProductInCart = false;
  isHacApp = false;
  invalidShareLink = false;

  private readonly _profileProvider = inject(CHECKOUT_PROFILE_PROVIDER);

  constructor(
    private readonly _apiService: ApiService,
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _modal: NzModalService,
    private readonly _notification: NzNotificationService,
    private readonly sessionService: SessionService,
    private readonly _router: Router,
    private readonly _drawerService: NzDrawerService,
    private readonly _http: HttpClient,
    private readonly _authStateService: AuthStateService,
    private readonly _cartStateService: CartStateService,
    private readonly _filesService: FilesService
  ) {}

  ngOnInit(): void {
    this.isHacApp = this._authStateService.getAppType() === 'hac';
    // For HAC app, always set numberOfTickets to 1
    if (this.isHacApp) {
      this.numberOfTickets = 1;
    }

    const slug: string = this._activatedRoute.snapshot.params['slug'];
    const token: string | undefined =
      this._activatedRoute.snapshot.params['token'];
    const uuid: string | undefined =
      this._activatedRoute.snapshot.params['uuid'];

    if (token) {
      // Share-link flow: uid is not in the URL, must wait for the
      // share-token response before fanning out to fields/ticket-types.
      this.loadEventByShareToken(slug, token);
    } else if (uuid) {
      // Legacy slug/uuid/eventTypeUid flow: uid is already known so all
      // three calls run in parallel.
      this.getEvent();
      this.getEventTicketType(uuid);
      this.getEventFields(uuid);
    }

    this.getSessionId();
  }

  private loadEventByShareToken(slug: string, token: string): void {
    this._apiService.getEventByShareToken(slug, token).subscribe({
      next: (res: HttpResponse<EventModel>) => {
        if (!res.body) return;
        this.selectedEvent = res.body;
        // Unpublished events are rendered as a standalone "not available"
        // message — skip image, fields, and ticket-type fetches entirely.
        if (!res.body.published) return;
        this.loadEventImageUrl(res.body.imageFileName);
        this.getEventFields(res.body.uid);
        this.getEventTicketType(res.body.uid);
        setTimeout(() => this.scrollToTop(), 100);
      },
      error: (err) => {
        if (err?.status === 404) {
          this.invalidShareLink = true;
        }
      },
    });
  }

  public updateNumberOfTickets(num: number): void {
    if (this.hasSelectedAddOns) {
      this.numberOfTickets = 1;
      return;
    }

    // For HAC app, prevent incrementing beyond 1
    if (this.isHacApp && this.numberOfTickets >= 1 && num === 1) {
      return;
    }

    if (num === 1) {
      this.numberOfTickets++;
    } else {
      if (this.numberOfTickets === 1) return;
      this.numberOfTickets--;
    }
  }

  public getEvent(): void {
    const slug: string = this._activatedRoute.snapshot.params['slug'];
    if (!slug) return;
    this._apiService.getEventBySlug(slug).subscribe({
      next: (res: HttpResponse<EventModel>) => {
        if (res.body) {
          this.selectedEvent = res.body;
          // Load the image URL
          this.loadEventImageUrl(res.body.imageFileName);
          // Scroll to top after content is loaded, accounting for fixed header
          setTimeout(() => this.scrollToTop(), 100);
        }
      },
    });
  }

  public viewIndemity(e: MouseEvent): void {
    e.stopPropagation();

    let width = '100%';
    if (window.innerWidth <= 952) {
      width = '100%';
    } else if (window.innerWidth > 952 && window.innerWidth <= 1150) {
      width = '90%';
    } else if (innerWidth > 1400) {
      width = '1200px';
    }

    this._drawerService.create<IndemityComponent>({
      nzTitle: undefined,
      nzContent: IndemityComponent,
      nzWidth: width,
    });
  }

  public getEventFields(uuid?: string): void {
    const eventUid: string =
      uuid ?? this._activatedRoute.snapshot.params['uuid'];
    if (!eventUid) return;
    this._apiService.getEventFieldsByEventUuid(eventUid).subscribe({
      next: (res: HttpResponse<EventField[]>) => {
        if (res.body) {
          this.fields = [...res.body].sort(
            (a, b) =>
              (a.positionOrder ?? Number.POSITIVE_INFINITY) -
              (b.positionOrder ?? Number.POSITIVE_INFINITY)
          );
          this.loadAttendeePrefillProfile();
        }
      },
    });
  }

  /**
   * Load the profile used to pre-fill the AddToCart modal's attendee
   * fields. HAC uses its own member-details endpoint (member number is
   * the lookup key); every other app reads from `CHECKOUT_PROFILE_PROVIDER`
   * which is bound to the running app's customer profile service.
   */
  private loadAttendeePrefillProfile(): void {
    if (
      this._authStateService.isAuthenticated() &&
      this._authStateService.getAppType() === 'hac' &&
      this._authStateService.getHacMemberNumber()
    ) {
      this.getUserProfile();
      return;
    }

    const profile = this._profileProvider.profile();
    if (profile) {
      this.userProfile = profile;
    }
  }

  public getEventTicketType(eventUuid?: string): void {
    const uuid: string =
      eventUuid ?? this._activatedRoute.snapshot.params['uuid'];
    const preselectedTicketTypeUid: string | null =
      this._activatedRoute.snapshot.queryParamMap.get('ticketTypeUid');
    if (!uuid) return;
    this.loadSeatMap(uuid);
    this.loadingTickets = true;
    this._apiService.getAllEventTicketTypesByEventUuid(uuid).subscribe({
      next: (res: HttpResponse<EventTicketType[]>) => {
        if (res.body) {
          // Sort tickets: free tickets first, then by price ascending
          this.eventTickets = res.body.sort((a, b) => {
            // If both are free or both are paid, sort by price
            if (a.free === b.free) {
              return a.price - b.price;
            }
            // Free tickets come first
            return a.free ? -1 : 1;
          });

          if (preselectedTicketTypeUid) {
            const preselected = this.eventTickets.find(
              (ticket) => ticket.uid === preselectedTicketTypeUid
            );
            if (preselected) {
              this.onTicketSelected(preselected);
            }
          }
        }
        this.loadingTickets = false;
      },

      error: () => {
        this.loadingTickets = false;
      },
      complete: () => {
        this.loadingTickets = false;
      },
    });
  }

  /**
   * The map is an addition to the ticket list, never a requirement: an event without one, or a map that
   * fails to load, simply shows the list as before.
   */
  private loadSeatMap(eventUid: string): void {
    this.seatMap = null;
    this.loadingSeatMap = true;
    // The map is optional, so it must never hold up buying: give up on it after a few seconds.
    this._apiService.getEventSeatMap(eventUid).pipe(timeout(SEAT_MAP_TIMEOUT_MS)).subscribe({
      next: (map) => {
        this.seatMap = map && map.bays.length > 0 ? map : null;
        this.loadingSeatMap = false;
      },
      error: () => {
        this.seatMap = null;
        this.loadingSeatMap = false;
      },
    });
  }

  /** The ticket section waits for the tickets and the map together, so the map cannot arrive late and push it down. */
  get ticketsAreLoading(): boolean {
    return this.loadingTickets || this.loadingSeatMap;
  }

  private addableUidsKey = '';
  private addableUids: string[] = [];

  /**
   * Ticket types that can be bought now, for the map. Kept as one array until the set changes, so the
   * map is not told "new input" on every change-detection pass.
   */
  get addableTicketTypeUids(): string[] {
    const uids = (this.eventTickets ?? [])
      .filter((ticket) => isTicketAddable(ticket, this.selectedEvent?.status))
      .map((ticket) => ticket.uid);
    const key = uids.join(',');
    if (key !== this.addableUidsKey) {
      this.addableUidsKey = key;
      this.addableUids = uids;
    }
    return this.addableUids;
  }

  /**
   * After picking a bay the quantity and Add to Cart controls can be a screen or more below the map on
   * a wide layout. Take the customer there, and move focus with the view so a keyboard or screen reader
   * user lands on the same place.
   */
  public scrollToTickets(): void {
    const heading = document.getElementById('tickets-heading');
    if (!heading) return;
    const reduceMotion =
      typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
    // Focus first: moving focus after starting a smooth scroll can cancel the scroll in some browsers.
    heading.focus({ preventScroll: true });
    heading.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
  }

  /** A bay stands for its ticket type: picking one selects that ticket type, as the list would. */
  public onBayPicked(bay: SeatMapBay): void {
    const ticketType = this.eventTickets?.find(
      (ticket) => ticket.uid === bay.ticketTypeUid
    );
    if (
      ticketType &&
      isTicketAddable(ticketType, this.selectedEvent?.status)
    ) {
      this.onTicketSelected(ticketType);
    }
  }

  public onTicketSelected(ticketType: EventTicketType) {
    this.selectedTicketType = ticketType;
    this.numberOfTickets = 1;
    this.ticketTypeAddOns = [];
    this.selectedAddOnUids.clear();
    this.loadTicketTypeAddOns(ticketType.uid);
    // Check if product is in cart when ticket is selected (HAC only)
    if (this.isHacApp) {
      this.checkIfProductInCart();
    }
  }

  get hasConfiguredAddOns(): boolean {
    return this.ticketTypeAddOns.length > 0;
  }

  get hasSelectedAddOns(): boolean {
    return this.selectedAddOnUids.size > 0;
  }

  get selectedAddOnsCount(): number {
    return this.selectedAddOnUids.size;
  }

  get selectedAddOnsTotal(): number {
    return this.ticketTypeAddOns
      .filter((addOn) => this.selectedAddOnUids.has(addOn.uid))
      .reduce((total, addOn) => total + addOn.amount, 0);
  }

  get selectedAddOnsCurrencyCode(): string {
    const selectedAddOn = this.ticketTypeAddOns.find((addOn) =>
      this.selectedAddOnUids.has(addOn.uid)
    );
    return (
      selectedAddOn?.currencyCode ||
      this.selectedTicketType?.currencyCode ||
      'USD'
    );
  }

  get selectedTicketTotalCurrencyCode(): string {
    return (
      this.selectedTicketType?.currencyCode || this.selectedAddOnsCurrencyCode
    );
  }

  get selectedTicketTotal(): number {
    const ticketPrice = this.selectedTicketType?.free
      ? 0
      : this.selectedTicketType?.price ?? 0;
    return ticketPrice + this.selectedAddOnsTotal;
  }

  public isAddOnSelected(addOn: TicketTypeAddOn): boolean {
    return this.selectedAddOnUids.has(addOn.uid);
  }

  public onAddOnToggle(addOn: TicketTypeAddOn, checked: boolean): void {
    if (addOn.required) {
      this.selectedAddOnUids.add(addOn.uid);
      this.numberOfTickets = 1;
      return;
    }

    if (checked) {
      this.selectedAddOnUids.add(addOn.uid);
      this.numberOfTickets = 1;
    } else {
      this.selectedAddOnUids.delete(addOn.uid);
    }
  }

  public openAddOnsModal(): void {
    this.addOnsModalVisible = true;
  }

  public closeAddOnsModal(): void {
    this.addOnsModalVisible = false;
  }

  private loadTicketTypeAddOns(ticketTypeUid: string): void {
    this.loadingAddOns = true;
    this._apiService.getTicketTypeAddOns(ticketTypeUid).subscribe({
      next: (res: HttpResponse<TicketTypeAddOn[]>) => {
        this.ticketTypeAddOns = res.body ?? [];
        this.selectedAddOnUids = new Set(
          this.ticketTypeAddOns
            .filter((addOn) => addOn.required)
            .map((addOn) => addOn.uid)
        );
        if (this.hasSelectedAddOns) {
          this.numberOfTickets = 1;
        }
        this.loadingAddOns = false;
      },
      error: () => {
        this.ticketTypeAddOns = [];
        this.selectedAddOnUids.clear();
        this.loadingAddOns = false;
      },
      complete: () => {
        this.loadingAddOns = false;
      },
    });
  }

  private selectedAddOnsPayload(): TicketTypeAddOnSelection[] {
    return Array.from(this.selectedAddOnUids).map((uid) => ({
      uid,
      quantity: 1,
    }));
  }

  /**
   * True when a ticket type is selected but is no longer addable (closed,
   * sold out, or no availability). Used to defensively block the Add-to-Cart
   * button even though the selection gate should already prevent it.
   */
  get selectedTicketBlocked(): boolean {
    return (
      !!this.selectedTicketType &&
      !isTicketAddable(this.selectedTicketType, this.selectedEvent?.status)
    );
  }

  public addToCart(): void {
    // For HAC app, prevent adding if product is already in cart
    if (this.isHacApp && this.isProductInCart) {
      this._notification.warning(
        'Already in Cart',
        'As a member, you can only buy 1 ticket per event. This ticket is already in your cart.',
        { nzDuration: 5000, nzAnimate: true }
      );
      return;
    }

    if (!this.selectedTicketType) {
      this._notification.warning(
        'Warning',
        'First select the ticket type to add to cart',
        { nzDuration: 5000, nzAnimate: true }
      );
      return;
    }

    if (!isTicketAddable(this.selectedTicketType, this.selectedEvent?.status)) {
      this._notification.warning(
        'Unavailable',
        'This ticket type is no longer available for purchase.',
        { nzDuration: 5000, nzAnimate: true }
      );
      return;
    }

    if (this.fields.length === 0 || !this.fields) {
      this.addProductWithoutFieldsToCart();
      return;
    }

    const addedWithAddOns = this.hasSelectedAddOns;
    const modalRef: NzModalRef = this._modal.create<
      AddToCartComponent,
      {
        fields: EventField[];
        parentUid: string;
        productUid: string;
        selectedAddOns: TicketTypeAddOnSelection[];
        userProfile: any;
      }
    >({
      nzContent: AddToCartComponent,
      nzTitle: 'Additional Information Required',
      nzData: {
        fields: this.fields,
        parentUid: this.selectedEvent.uid,
        productUid: this.selectedTicketType.uid,
        selectedAddOns: this.selectedAddOnsPayload(),
        // Re-read at modal-open time so we always pick up the latest
        // loaded profile (HAC member details for HAC, customer profile
        // for everything else). loadAttendeePrefillProfile populates
        // this.userProfile during getEventFields, but the customer
        // profile may finish loading after that point — fall back to
        // the provider here.
        userProfile: this.userProfile ?? this._profileProvider.profile(),
      },
    });

    modalRef.afterClose.subscribe((val: boolean) => {
      if (val) {
        // The add (incl. any stale-cart recovery) already updated the cached
        // cart in CartStateService. Re-sync our local copy so getCartItems()
        // and cart navigation use the current UID, not a stale one.
        this.cart = this._cartStateService.cart() ?? this.cart;
        // Refresh cart items for HAC app to update isProductInCart status
        if (this.isHacApp) {
          this.getCartItems();
        }
        this.clearTicketSelectionAfterAddOnsAdd(addedWithAddOns);
        this.checkAddMoreContent();
        this._notification.success('Success', 'Tickets added to cart', {
          nzAnimate: true,
          nzDuration: 6000,
        });
      }
    });
  }

  public addProductWithoutFieldsToCart(): void {
    if (!this.selectedTicketType) {
      return;
    }

    // Add HAC member number to purchaseDetails if it's a HAC app and member number exists
    const purchaseDetails: { [key: string]: any } = {};
    if (this._authStateService.getAppType() === 'hac') {
      const hacMemberNumber = this._authStateService.getHacMemberNumber();
      if (hacMemberNumber) {
        purchaseDetails['hacMemberNumber'] = hacMemberNumber;
      }
    }

    const payload: AddToCartRequest = {
      parentUid: this.selectedEvent.uid,
      productUid: this.selectedTicketType.uid,
      purchaseDetails: purchaseDetails,
      quantity: this.numberOfTickets,
      selectedAddOns: this.selectedAddOnsPayload(),
    };
    const addedWithAddOns = this.hasSelectedAddOns;

    if (!this.cart || this.loadingCart) {
      this._notification.warning(
        'Cart not ready',
        'Please wait a moment while we prepare your cart, then try again.',
        { nzDuration: 4000, nzAnimate: true }
      );
      return;
    }

    this.loadingAddtocart = true;
    // Recovery-aware: a stale cart UID self-heals (rotate → fresh cart →
    // retry once). The wrapper owns notifyCartUpdate() on success.
    this._cartStateService.addToCartWithRecovery(payload, this.cart.uid).subscribe({
      next: () => {
        // Re-sync our local cart from the (now authoritative) cached cart so a
        // recovery-rotated UID is reflected before we read items / navigate.
        this.cart = this._cartStateService.cart() ?? this.cart;
        // Refresh cart items for HAC app to update isProductInCart status
        if (this.isHacApp) {
          this.getCartItems();
        }
        this.clearTicketSelectionAfterAddOnsAdd(addedWithAddOns);
        this.checkAddMoreContent();
        this.loadingAddtocart = false;
      },

      error: () => {
        this.loadingAddtocart = false;
      },
      complete: () => {
        this.loadingAddtocart = false;
      },
    });
  }

  public checkAddMoreContent(): void {
    this._modal.confirm({
      nzTitle: 'Product added to cart',
      nzContent: 'Do you want to proceed to cart',
      nzOkText: 'Yes Proceed to cart',
      nzCancelText: 'No add more tickets',
      nzOnOk: () => this.proceedToCart(),
      nzOnCancel: () => {
        if (this.isHacApp) {
          // HAC enforces one ticket per event per member, so once a ticket
          // is added the user is sent back to browse other events.
          this._router.navigate(['/']).then(() => {
            setTimeout(() => {
              const eventsSection = document.getElementById('active-events');
              if (eventsSection) {
                eventsSection.scrollIntoView({
                  behavior: 'smooth',
                  block: 'start',
                });
              }
            }, 100);
          });
        }
        // Non-HAC: stay on the current event page so the user can re-open
        // the AddToCart modal and add another ticket with fresh field
        // values (one ticket per submission, since each attendee needs
        // their own registration fields). Nothing to do here — the
        // confirm dialog has already closed.
      },
    });
  }

  private clearTicketSelectionAfterAddOnsAdd(addedWithAddOns: boolean): void {
    if (!addedWithAddOns) {
      return;
    }

    this.selectedTicketType = null;
    this.ticketTypeAddOns = [];
    this.selectedAddOnUids.clear();
    this.addOnsModalVisible = false;
    this.numberOfTickets = 1;
    this.isProductInCart = false;
  }

  public proceedToCart(): void {
    if (!this.cart) return;
    // Soft navigation keeps CartStateService cache alive across the route change.
    this._router.navigateByUrl('/cart/' + this.cart.uid);
  }

  public getSessionId(): void {
    this.loadingCart = true;
    this._cartStateService.resolveCart().subscribe({
      next: (cart) => {
        if (cart) {
          this.cart = cart;
          this.sessionId = cart.session?.uid?.toString() ?? this.sessionId;
          // Get cart items for HAC app to check if product is already in cart
          if (this.isHacApp && this.cart.uid) {
            this.getCartItems();
          }
        }
        this.loadingCart = false;
      },
      error: (err) => {
        console.error('Error resolving cart:', err);
        this.loadingCart = false;
      },
    });
  }

  public getCartItems(): void {
    if (!this.cart?.uid) return;

    this._apiService.getCartItemsByUuid(this.cart.uid).subscribe({
      next: (res) => {
        if (res.body) {
          this.cartItems = res.body;
          // Check if selected product is in cart
          if (this.selectedTicketType) {
            this.checkIfProductInCart();
          }
        }
      },
      error: () => {
        console.error('Error fetching cart items');
      },
    });
  }

  public checkIfProductInCart(): void {
    if (
      !this.selectedTicketType ||
      !this.cartItems ||
      this.cartItems.length === 0
    ) {
      this.isProductInCart = false;
      return;
    }

    const selectedTicketTypeUid = this.selectedTicketType.uid;

    // Check if the selected product UID matches any cart item's productUid
    // Also check if it's for the same event (parentUid)
    this.isProductInCart = this.cartItems.some(
      (item) =>
        item.productUid === selectedTicketTypeUid &&
        item.parentUid === this.selectedEvent?.uid
    );
  }

  public scrollToTop(): void {
    this._apiService.scrollToTop();
  }

  private loadEventImageUrl(fileName: string): void {
    if (!fileName) return;
    this._filesService.getFileDownloadUrl(fileName).subscribe({
      next: (url) => {
        this.eventImageUrl = url;
      },
      error: () => {
        this.eventImageUrl = 'assets/placeholder.jpg';
      },
    });
  }

  public getUserProfile(): void {
    // Get HAC member number from session storage
    const memberNumber = this._authStateService.getHacMemberNumber();
    if (!memberNumber) {
      return;
    }

    const endpoint = `${this.apiUrl}/${Microservice.CRM}/opn/v1/hac-members/member-number/${memberNumber}/details`;

    const httpOptions = {
      headers: new HttpHeaders({
        Accept: '*/*',
      }),
      observe: 'response' as const,
    };

    this._http.get<any>(endpoint, httpOptions).subscribe({
      next: (res: HttpResponse<any>) => {
        if (res.body) {
          this.userProfile = res.body;
        }
      },
      error: (err) => {
        console.error('Error fetching HAC member details:', err);
      },
    });
  }
}
